/* eslint-disable indent */
/**
 *
 * InputField
 *
 */

import { Box } from '@mui/material';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { withTheme } from 'styled-components';

import { CheckCircleIcon, ErrorIcon } from 'assets/v2/icons';
import { COLORS } from 'theme';

import {
  DateMask,
  MedicaidIdMask,
  NpiMask,
  PhoneNumberMask,
  TaxIdMask,
  TimeMask,
} from '../InputMask';
import { StyledInputAdornment } from './InputField.styles';

export const ADORNMENT_PLACES = {
  LEFT: 'left',
  RIGHT: 'right',
};

export const MASKS = {
  date: DateMask,
  time: TimeMask,
  phone: PhoneNumberMask,
  taxId: TaxIdMask,
  npi: NpiMask,
  medicaidId: MedicaidIdMask,
};

export const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  XS: 'xs',
};

const InputField = ({
  value,
  label,
  id,
  adornmentPlace = ADORNMENT_PLACES.LEFT,
  adornment,
  helperText,
  size = SIZES.MEDIUM,
  valid,
  error,
  success,
  required,
  disabled,
  readOnly,
  showRequiredIconPlaceholder,
  theme,
  placeholder,
  sx,
  InputProps,
  mask,
  overrideComponentColor,
  overrideIsValueEmpty,
  onChange,
  onBlur,
  type,
  unit = null, // @param {string|null} -  Optional unit to display alongside the input value (e.g., "miles", "minutes")
  EndAdornmentIcon, // @param {ReactElement} - Optional status icon (e.g. "this field changed"); outranked by the error icon
  ...props
}) => {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const getHelperText = () => {
    if (!active && valid === false && error) {
      if (typeof error === 'object') {
        return (
          <Stack spacing={0.5}>
            {Object.values(error).map((errorMessage) => (
              <div>{errorMessage}</div>
            ))}
          </Stack>
        );
      }
      return error;
    }
    if (valid && success) {
      return success;
    }
    return helperText;
  };

  const componentColor = useMemo(() => {
    if (disabled || active) return 'primary';

    switch (valid) {
      case true:
        return 'success';
      case false:
        return 'error';
      default:
        return 'primary';
    }
  }, [disabled, valid, active]);

  const [helper, setHelper] = useState(getHelperText());
  const isAdornmentPlaceOverride =
    adornmentPlace === ADORNMENT_PLACES.LEFT &&
    (adornment || showRequiredIconPlaceholder);
  const isMedium = size === SIZES.MEDIUM;
  const isXs = size === SIZES.XS;
  const customInputProps = { ...InputProps };

  if (MASKS[mask]) {
    customInputProps.inputComponent = MASKS[mask];
  }

  const isValueEmpty =
    (!value && value !== 0) || (typeof value === 'object' && _.isEmpty(value));

  useEffect(() => {
    setHelper(getHelperText());
  }, [valid, error, helperText, active]);

  useEffect(() => {
    setActive(false);
  }, [disabled]);

  const getEndAdornmentIcon = () => {
    let adornmentColor = COLORS.black30;
    if (valid === true) {
      adornmentColor = COLORS.green;
    }
    if (valid === false) {
      adornmentColor = COLORS.red;
    }

    if (adornment) {
      return (
        <Box
          sx={{
            path: {
              fill: adornmentColor,
            },
          }}
        >
          {adornment}
        </Box>
      );
    }
    // Errors outrank every other status icon
    if (valid === false) {
      return <ErrorIcon sx={{ color: adornmentColor }} />;
    }
    // Then any caller-supplied status icon (e.g. "this field changed")
    if (EndAdornmentIcon) {
      return EndAdornmentIcon;
    }
    if (valid === true) {
      return <CheckCircleIcon sx={{ color: adornmentColor }} />;
    }
    if (required && showRequiredIconPlaceholder) {
      return <ErrorIcon sx={{ color: adornmentColor }} />;
    }
    return null;
  };

  // Returns the end adornment for the input field.
  // If a `unit` is provided, wraps the unit and any additional adornment inside a styled InputAdornment.
  // Otherwise, just renders the additional adornment directly.
  const getAdornment = () =>
    unit ? (
      <StyledInputAdornment position="end">
        <span>{unit}</span>
        {getEndAdornmentIcon()}
      </StyledInputAdornment>
    ) : (
      getEndAdornmentIcon()
    );

  const getAdornmentColor = useMemo(() => {
    let adornmentColor = COLORS.black50;
    if (disabled) {
      adornmentColor = COLORS.black30;
    } else if (hover || active) {
      adornmentColor = COLORS.kinetikBlack;
    }
    return adornmentColor;
  }, [disabled, hover, active]);

  // Only ever one icon in the slot. A status icon takes the slot over entirely,
  // replacing whatever endAdornment the caller supplied (a date picker's open
  // button, etc.) rather than sitting beside it. Priority within getAdornment()
  // is error icon, then the status icon, then the field's normal adornment.
  const getEndAdornment = () => {
    if (!EndAdornmentIcon && customInputProps.endAdornment) {
      return customInputProps.endAdornment;
    }

    // Status icons persist while the field is focused — a "changed" marker
    // shouldn't disappear the moment the user clicks in to fix it
    return (!active || adornment || EndAdornmentIcon) && getAdornment();
  };

  return (
    <TextField
      onFocus={() => !disabled && setActive(true)}
      onBlur={() => {
        onBlur?.();
        if (!disabled) {
          setActive(false);
        }
      }}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => !disabled && setHover(false)}
      value={value != null ? value : '' || props.inputProps?.value}
      label={label}
      id={id}
      helperText={helper}
      size={size}
      InputProps={{
        readOnly,
        className: value != null ? 'MuiInputBase-filled' : undefined,
        ...customInputProps,
        // Resolved after the spread — getEndAdornment already accounts for
        // whatever endAdornment the caller passed
        endAdornment: getEndAdornment(),
      }}
      InputLabelProps={{
        color: componentColor,
      }}
      fullWidth
      error={disabled ? undefined : valid === false}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      color={componentColor}
      sx={{
        '.MuiFormLabel-root': {
          fontSize: isMedium ? theme.fontSize.body1 : theme.fontSize.body3,
          fontWeight: 500,
          left: isAdornmentPlaceOverride ? theme.spacing.m4 : '2px',
          ...(hover &&
            valid === undefined && {
              '&[data-shrink="false"]': {
                color: COLORS.kinetikBlack,
              },
            }),
          ...(active && {
            '& .MuiFormLabel-asterisk': {
              color: COLORS.black30,
            },
          }),
        },
        '.MuiInputLabel-root': {
          transform: `translate(14px, ${theme.spacing.s4})`,
          ...(overrideComponentColor && {
            color: `${overrideComponentColor} !important`,
          }),
          '&.MuiInputLabel-shrink': {
            transform: `translate(${
              isAdornmentPlaceOverride ? '17px' : '14px'
            }, 8px) scale(${isMedium ? 0.75 : 1})`,
            '+ .MuiInputBase-root': {
              input: {
                padding: `${theme.spacing.m2} ${
                  isAdornmentPlaceOverride ? theme.spacing.s2 : theme.spacing.s4
                } ${theme.spacing.s2}`,
                transition: 'padding 0.2s',
              },
            },
          },
        },
        '.MuiInputBase-root': {
          backgroundColor: COLORS.white,
          fieldset: {
            top: 0,
            legend: {
              position: 'absolute',
            },
            ...(overrideComponentColor && {
              borderColor: `${overrideComponentColor} !important`,
            }),
          },
          ...(isAdornmentPlaceOverride && {
            display: 'flex',
            flexDirection: 'row-reverse',
          }),
          fontSize: theme.fontSize.body3,
          ...(isXs
            ? {
                '.MuiInputBase-input': {
                  fontSize: theme.fontSize.body2,
                  fontWeight: theme.fontWeight.regular,
                  padding: `6px ${theme.spacing.s3}`,
                },
              }
            : {
                '.MuiInputBase-input': {
                  fontSize: isMedium
                    ? theme.fontSize.body1
                    : theme.fontSize.body3,
                  height: isMedium
                    ? theme.lineHeight.body1
                    : theme.lineHeight.body3,
                  fontWeight: 500,
                  padding: isAdornmentPlaceOverride
                    ? `${theme.spacing.s4} 9px`
                    : `${theme.spacing.s4} ${theme.spacing.s4}`,
                },
              }),

          '&.Mui-focused, &:not(.Mui-disabled):not(.Mui-error):hover': {
            '+ .MuiFormHelperText-root': {
              color: COLORS.kinetikBlack,
            },
          },
          // If the input field is not explicitly valid, and the root-level value is nullish but not an empty string OR the sub-input-level value is nullish but not an empty string.
          ...(valid === undefined &&
          !active &&
          !overrideIsValueEmpty &&
          (!isValueEmpty ||
            (props.inputProps?.value != null && props.inputProps?.value !== ''))
            ? {
                ':not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline:not(.Mui-disabled)':
                  {
                    borderColor: COLORS.kinetikBlack,
                  },
              }
            : null),
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.kinetikBlue,
          },
          '&.MuiInputBase-colorError': {
            '+ .MuiFormHelperText-root': {
              color: COLORS.redDark,
            },
          },
          '.div, button': {
            '.MuiSvgIcon-root': {
              path: {
                fill: getAdornmentColor,
              },
            },
          },
        },
        '&:not(.Mui-error)': {
          '.MuiSvgIcon-root': {
            marginLeft: isAdornmentPlaceOverride ? theme.spacing.s4 : 0,
            width: theme.spacing.m2,
          },
        },
        '.MuiSvgIcon-root': {
          marginLeft: isAdornmentPlaceOverride ? theme.spacing.s4 : 0,
          ...(overrideComponentColor && {
            color: overrideComponentColor,
          }),
        },
        '.MuiFormHelperText-root': {
          textAlign: 'left',
          margin: `2px ${theme.spacing.s4}`,
          lineHeight: theme.lineHeight.body3,
        },
        ...sx,
      }}
      onChange={(event) => {
        if (typeof event === 'string') {
          onChange?.(event);
          return;
        }
        if (
          type === 'number' &&
          (!event?.nativeEvent?.inputType ||
            ['e', 'E', '+', '-'].includes(event?.nativeEvent?.data)) // NOTE: Filter out scientific notation and other non-numeric characters
        ) {
          return;
        }
        onChange?.(event);
      }}
      type={type}
      {...props}
    />
  );
};

InputField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  id: PropTypes.string,
  adornmentPlace: PropTypes.oneOf(Object.values(ADORNMENT_PLACES)),
  adornment: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  helperText: PropTypes.string,
  size: PropTypes.oneOf(Object.values(SIZES)),
  valid: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  success: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  showRequiredIconPlaceholder: PropTypes.bool,
  theme: PropTypes.object,
  inputProps: PropTypes.object,
  placeholder: PropTypes.string,
  InputProps: PropTypes.object,
  sx: PropTypes.object,
  mask: PropTypes.oneOf(Object.keys(MASKS)),
  overrideComponentColor: PropTypes.string,
  overrideIsValueEmpty: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  type: PropTypes.string,
  unit: PropTypes.string,
  EndAdornmentIcon: PropTypes.element,
};

export default memo(withTheme(InputField));
